import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import ArtistCard from "../components/ArtistCard";
import { FALLBACK_CATEGORIES, FALLBACK_CITIES } from "../constants/mcevents";
import { ArrowRight, Search, MapPin, Sparkles, UserSearch, MessagesSquare, FileSignature, PartyPopper } from "lucide-react";
import { useContent } from "../contexts/ContentContext";

const HERO = "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1600&q=80";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_IMAGES = {
  chanteur: `${BACKEND}/api/files/98a9fd9c-2dfd-4e59-88a8-0a20e38276d0`,
  dj: `${BACKEND}/api/files/336e789b-a3d9-4cf4-8d6e-43635504f980`,
  danseur: `${BACKEND}/api/files/5f6360a5-e4ba-4907-af6a-8c5ed7625f21`,
  humoriste: `${BACKEND}/api/files/bfd0c31a-319d-4f1d-a1cd-0b09ff376d53`,
  groupe_traditionnel: `${BACKEND}/api/files/fa011fe3-5bed-42b4-ba40-40986d3b1088`,
  musicien: `${BACKEND}/api/files/907a0bf8-abf0-40bc-8389-07d576683242`,
  animateur: `${BACKEND}/api/files/f2895528-e4a7-4aac-a9f9-f8e0630a96c3`,
  rappeur: `${BACKEND}/api/files/0290d746-5cc8-4628-aace-495167a32fc1`,
  influenceur: `${BACKEND}/api/files/ff46f5d8-3d20-4ab3-b39b-a67727b6b345`,
  comedien: `${BACKEND}/api/files/a653475d-8555-4578-9552-093e95a0f5c0`,
  photographe: `${BACKEND}/api/files/b332cff8-58b0-4efa-b6d2-c0fc9cc017fa`,
  beatmaker: `${BACKEND}/api/files/59702bec-5f37-4e69-a951-420e86942540`,
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut", delay: i * 0.08 } }),
};

export default function Landing() {
  const { t, img } = useContent();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [searchCat, setSearchCat] = useState("");
  const [searchCity, setSearchCity] = useState("");

  useEffect(() => {
    api.get("/artists?limit=6").then((r) => setFeatured(r.data.artists || [])).catch(() => {});
  }, []);

  const hero = img("landing.hero_image", HERO);

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchCat) params.set("category", searchCat);
    if (searchCity) params.set("city", searchCity);
    navigate(`/explore?${params.toString()}`);
  };


  return (
    <div data-testid="landing-page" className="overflow-x-clip">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-border">
        <div className="orb orb-blue w-96 h-96 -top-32 -left-32" />
        <div className="orb orb-orange w-96 h-96 -bottom-40 -right-24" />
        <div className="container-mc grid grid-cols-1 lg:grid-cols-12 gap-12 py-16 lg:py-24 relative">
          <div className="lg:col-span-6 flex flex-col justify-center">
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
              {t("landing.title_line1", "Trouvez")}{" "}
              <span className="text-[hsl(var(--primary))]">{t("landing.title_highlight", "l'artiste idéal")}</span>{" "}
              {t("landing.title_line2_pre", "pour")}{" "}
              <span className="text-[hsl(var(--secondary))]">{t("landing.title_line2_red", "votre événement")}</span>.
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t("landing.subtitle", "Booking Artistes 224 met en relation le public et les artistes du pays pour des contrats de prestation simples, sécurisés et efficaces.")}
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap gap-3">
              <Link to="/explore" data-testid="hero-cta-explore" className="btn-primary">
                {t("landing.cta_explore", "Je suis organisateur")} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/register" data-testid="hero-cta-artist" className="btn-outline border-[hsl(var(--secondary))]/50 text-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/5 hover:border-[hsl(var(--secondary))]">
                {t("landing.cta_register", "Je suis artiste")}
              </Link>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
            className="lg:col-span-6 relative">
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="relative aspect-[16/11] overflow-hidden rounded-2xl border border-border shadow-[0_24px_64px_rgba(13,27,42,0.18)]">
              <img src={hero} alt="Artistes en scène" className="absolute inset-0 w-full h-full object-cover" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* BARRE DE RECHERCHE */}
      <section className="container-mc -mt-0 pt-14" data-testid="search-section">
        <motion.form onSubmit={submitSearch} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="card-mc p-6 sm:p-8 shadow-[0_8px_32px_rgba(13,27,42,0.08)]">
          <p className="font-display font-semibold text-lg mb-4">{t("landing.search_title", "Recherchez un artiste pour votre événement")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <select data-testid="search-category-select" value={searchCat} onChange={(e) => setSearchCat(e.target.value)}
                className="input-mc w-full pl-10 appearance-none cursor-pointer">
                <option value="">Type d'artiste — tous</option>
                {FALLBACK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="relative">
              <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <select data-testid="search-city-select" value={searchCity} onChange={(e) => setSearchCity(e.target.value)}
                className="input-mc w-full pl-10 appearance-none cursor-pointer">
                <option value="">Localisation — toute la Guinée</option>
                {FALLBACK_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button type="submit" data-testid="search-submit-btn" className="btn-primary">
              <Search className="w-4 h-4 mr-2" /> {t("landing.search_button", "Rechercher")}
            </button>
          </div>
        </motion.form>
      </section>

      {/* CATEGORIES */}
      <section className="container-mc py-20" data-testid="categories-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{t("landing.categories_title", "Explorez par catégorie")}</h2>
          <Link to="/explore" className="text-sm font-semibold text-[hsl(var(--primary))] hover:underline">{t("landing.categories_link", "Voir toutes les catégories →")}</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-5">
          {FALLBACK_CATEGORIES.map((c, i) => (
            <motion.div key={c.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i % 4}>
              <Link to={`/explore?category=${c.id}`} data-testid={`category-card-${c.id}`} className="group flex flex-col items-center gap-3 text-center">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-4 border-white shadow-[0_6px_20px_rgba(13,27,42,0.14)] ring-1 ring-border group-hover:ring-[hsl(var(--primary))] transition-all duration-300 group-hover:scale-105">
                  <img loading="lazy" src={img(`landing.cat_image_${c.id}`, CATEGORY_IMAGES[c.id] || HERO)} alt={c.label}
                    className="w-full h-full object-cover" />
                </div>
                <p className="text-sm font-semibold leading-tight group-hover:text-[hsl(var(--primary))] transition-colors duration-200">{c.label.split(" / ")[0]}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section id="how" className="relative bg-white border-y border-border py-20 overflow-hidden" data-testid="how-section">
        <div className="orb orb-blue w-96 h-96 -top-48 left-1/3" />
        <div className="container-mc relative">
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-center mb-14 uppercase">{t("landing.how_title", "Comment ça marche ?")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: "1", t: t("landing.how_step1_title", "Trouvez"), d: t("landing.how_step1_desc", "Recherchez l'artiste qui correspond à votre événement."), color: "hsl(var(--primary))" },
              { n: "2", t: t("landing.how_step2_title", "Contactez"), d: t("landing.how_step2_desc", "Échangez directement avec l'artiste et définissez vos besoins."), color: "hsl(var(--secondary))" },
              { n: "3", t: t("landing.how_step3_title", "Contractualisez"), d: t("landing.how_step3_desc", "Établissez un contrat de prestation en toute sécurité."), color: "hsl(var(--primary))" },
              { n: "4", t: t("landing.how_step4_title", "Réalisez"), d: t("landing.how_step4_desc", "Profitez de votre événement en toute sérénité !"), color: "hsl(var(--secondary))" },
            ].map(({ n, t: title, d, color }, i) => {
              const Icon = [UserSearch, MessagesSquare, FileSignature, PartyPopper][i];
              return (
              <motion.div key={n} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="relative flex flex-col items-center text-center gap-4 p-6">
                <div className="relative">
                  <motion.div whileHover={{ scale: 1.08, rotate: 3 }} transition={{ type: "spring", stiffness: 300 }}
                    className="w-20 h-20 rounded-full bg-muted flex items-center justify-center shadow-[inset_0_0_0_1px_rgba(13,27,42,0.06)]">
                    <Icon className="w-8 h-8" style={{ color }} />
                  </motion.div>
                  <span className="absolute -top-1.5 -left-4 w-9 h-9 rounded-full flex items-center justify-center font-display font-bold text-white text-sm shadow-lg"
                    style={{ background: color }}>
                    {n}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-xl">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
                {i < 3 && (
                  <div className="hidden lg:flex absolute top-9 -right-4 items-center text-[hsl(var(--primary))]/40" aria-hidden="true">
                    <span className="block w-8 border-t-2 border-dashed border-current" />
                    <ArrowRight className="w-4 h-4 -ml-1" />
                  </div>
                )}
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ARTISTES POPULAIRES */}
      {featured.length > 0 && (
        <section className="container-mc py-20" data-testid="featured-section">
          <div className="flex justify-between items-end mb-10">
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{t("landing.featured_title", "Artistes populaires")}</h2>
            <Link to="/explore" className="text-sm font-semibold text-[hsl(var(--primary))] hover:underline">Tous →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.slice(0, 6).map((a) => <ArtistCard key={a.id} artist={a} />)}
          </div>
        </section>
      )}

      {/* CTA ARTISTE */}
      <section className="container-mc py-20" data-testid="cta-section">
        <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative overflow-hidden rounded-3xl bg-foreground text-background p-10 md:p-16">
          <div className="orb orb-orange w-96 h-96 -right-24 -top-32 opacity-80" />
          <div className="orb orb-blue w-96 h-96 -left-24 -bottom-32 opacity-80" />
          <div className="absolute top-0 inset-x-0 gn-strip h-[3px]" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-8">
              <Sparkles className="w-8 h-8 text-[hsl(var(--secondary))] mb-5" />
              <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-tight">
                {t("landing.cta_block_title_line1", "Vous êtes artiste ?")}<br />
                <span className="text-[hsl(var(--secondary))]">{t("landing.cta_block_title_line2", "Mettez en avant votre talent.")}</span>
              </h2>
              <p className="mt-5 text-background/70 max-w-xl leading-relaxed">{t("landing.cta_block_subtitle", "Publiez votre profil, partagez vos extraits musicaux et recevez des sollicitations payantes des clients de toute la Guinée.")}</p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link to="/register" className="btn-secondary text-lg" data-testid="cta-register-artist">{t("landing.cta_block_button", "Inscription artistes →")}</Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
